import './App.css';
import React, { useState, useEffect, useCallback } from 'react';
import LoginForm from './LoginForm';
import Register from './Register';
import TaskList from './TaskList';
import CreateTaskForm from './CreateTaskForm';
import MyTasks from './MyTasks';
import MyCreatedTasks from './MyCreatedTasks';
import UserMenu from './UserMenu';
import AdminPanel from './AdminPanel';
import UserBadge from './UserBadge';
import Footer from './Footer';
import ResetPasswordConfirmPage from './ResetPasswordConfirmPage';
import NotificationsDropdown from './NotificationsDropdown';
import NewsModal from './NewsModal';
import CommunityChat from './CommunityChat';
import MessageListener from './MessageListener';

import logo from './assets/logo.png';

// ИМПОРТ ИКОНОК
import iconNews from './assets/icon-news.png';
import iconExit from './assets/icon_exit.png';

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState('executor');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // Триггер для бесшовного обновления списков задач во всех компонентах
  const [tasksRefreshTrigger, setTasksRefreshTrigger] = useState(0);

  const refreshTasks = useCallback(() => {
    setTasksRefreshTrigger(prev => prev + 1);
  }, []);

  const [isNewsOpen, setIsNewsOpen] = useState(false);
  const [resetParams, setResetParams] = useState(null);

  const isAdmin = user && user.is_staff;

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  // Перехват URL для сброса пароля при загрузке
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('reset-password')) {
      const parts = path.split('/').filter(Boolean);
      if (parts.length >= 3) {
        setResetParams({
          uid: parts[1],
          token: parts[2]
        });
      }
    }
  }, []);

  const handleForceLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Валидация сессии при загрузке приложения
  useEffect(() => {
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (!storedUser || !token) {
        handleForceLogout();
        setIsAuthChecking(false);
        return;
      }

      try {
        const response = await fetch('/api/users/me/', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          console.warn("Сессия авторизации истекла.");
          handleForceLogout();
        }
      } catch (error) {
        console.error("Ошибка при проверке авторизации:", error);
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } finally {
        setIsAuthChecking(false);
      }
    };

    if (!resetParams) {
      checkAuth();
    } else {
      setIsAuthChecking(false);
    }
  }, [resetParams]);

  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light-theme');
    } else {
      document.documentElement.classList.remove('light-theme');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (resetParams) {
    return (
      <div 
        style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-main)' }} 
        className="min-h-screen relative font-sans transition-colors duration-300"
      >
        <div className="fixed top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="fixed bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
        
        <ResetPasswordConfirmPage 
          uid={resetParams.uid} 
          token={resetParams.token} 
        />
        <Footer />
      </div>
    );
  }

  if (isAuthChecking) {
    return (
      <div 
        style={{ backgroundColor: 'var(--bg-main)' }} 
        className="min-h-screen flex items-center justify-center"
      >
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div 
       style={{ backgroundColor: 'var(--bg-main)', color: 'var(--text-main)' }} 
       className="min-h-screen relative font-sans transition-colors duration-300 overflow-x-hidden"
    >
      <div className="fixed top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      
      {/* НАВИГАЦИЯ */}
      <nav className="relative z-20 p-3 sm:p-4 flex flex-wrap justify-between items-center gap-2 sm:gap-4 px-3 sm:px-6 border-b border-white/5 bg-slate-950/20 backdrop-blur-md">
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {user && <UserBadge />}
        </div>
        
        {/* Правый блок: новости + сообщения + выход */}
        {user && (
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            
            {/* КНОПКА НОВОСТЕЙ */}
            <button
              onClick={() => setIsNewsOpen(true)}
              className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm bg-slate-900/40 backdrop-blur-md border border-white/10 text-slate-200 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl hover:bg-indigo-500/20 hover:text-indigo-200 hover:border-indigo-400/40 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),0_8px_20px_rgba(0,0,0,0.3)] transition-all duration-300 active:scale-95 cursor-pointer whitespace-nowrap"
            >
              <img 
                src={iconNews} 
                alt="Новости" 
                className="w-5 h-5 sm:w-7 sm:h-7 object-contain drop-shadow-md" 
              />
              <span className="font-semibold hidden xs:inline sm:inline">Новости</span>
            </button>

            <NotificationsDropdown />

            {/* КНОПКА ВЫХОДА */}
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm bg-slate-900/40 backdrop-blur-md border border-white/10 text-slate-200 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl hover:bg-rose-500/20 hover:text-rose-300 hover:border-rose-400/40 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),0_8px_20px_rgba(0,0,0,0.3)] transition-all duration-300 active:scale-95 cursor-pointer whitespace-nowrap"
            >
              <img 
                src={iconExit} 
                alt="Выйти" 
                className="w-5 h-5 sm:w-7 sm:h-7 object-contain drop-shadow-md" 
              />
              <span className="font-semibold hidden xs:inline sm:inline">Выйти</span>
            </button>
          </div>
        )}
      </nav>

      {/* Модальное окно новостей */}
      {isNewsOpen && <NewsModal onClose={() => setIsNewsOpen(false)} />}
      
      <div className="relative z-10 p-3 sm:p-4">
        {!user ? (
          <div>
            {isRegistering ? (
              <Register onSwitchToLogin={() => setIsRegistering(false)} />
            ) : (
              <LoginForm 
                setUser={setUser} 
                onSwitchToRegister={() => setIsRegistering(true)} 
              />
            )}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto py-6 sm:py-10 pb-40">
            
            {/* КРУПНЫЙ ЦЕНТРИРОВАННЫЙ ЛОГОТИП */}
            <div className="flex justify-center mb-6 select-none animate-in fade-in slide-in-from-top-4 duration-1000">
              <img 
                src={logo} 
                alt="Realwork.pro" 
                className="h-12 sm:h-16 md:h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(99,102,241,0.25)]" 
              />
            </div>

            <h1 
              style={{ color: 'var(--text-title)' }} 
              className="text-2xl sm:text-3xl font-extrabold text-center mb-6 sm:mb-8 tracking-tight transition-colors duration-300"
            >
              Панель управления
            </h1>

            {/* СЕГМЕНТНЫЙ ПЕРЕКЛЮЧАТЕЛЬ B СТИЛЕ LIQUID GLASS */}
            <div className="flex flex-col items-center mb-8 sm:mb-10">
  <div className="relative flex p-1.5 bg-slate-900/50 backdrop-blur-xl rounded-2xl w-full max-w-xs border border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_10px_25px_rgba(0,0,0,0.4)]">
    
    {/* Плавающий бегунок */}
    <div 
      className={`absolute top-1.5 bottom-1.5 left-1.5 w-[calc(50%-6px)] bg-indigo-600/70 backdrop-blur-md border border-indigo-400/30 rounded-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.3),0_4px_12px_rgba(0,0,0,0.3)] transition-transform duration-300 ease-out ${
        role === 'customer' ? 'translate-x-full' : 'translate-x-0'
      }`} 
    />

    <button 
      onClick={() => setRole('executor')} 
      className={`relative z-10 flex-1 py-2 text-sm font-bold rounded-xl transition-all duration-200 text-center cursor-pointer active:scale-95 select-none ${
        role === 'executor' ? 'text-white' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      Исполнитель
    </button>

    <button 
      onClick={() => setRole('customer')} 
      className={`relative z-10 flex-1 py-2 text-sm font-bold rounded-xl transition-all duration-200 text-center cursor-pointer active:scale-95 select-none ${
        role === 'customer' ? 'text-white' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      Заказчик
    </button>
  </div>
</div>

{/* КОНТЕНТ С ПРЯМОЙ CSS-АНИМАЦИЕЙ */}
<div 
  key={role} 
  className="tab-content-anim"
>
  {role === 'executor' ? (
    <div className="space-y-6 sm:space-y-8">
      <div 
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-card)' }} 
        className="backdrop-blur-md p-4 sm:p-6 rounded-3xl border shadow-2xl shadow-black/20 transition-all duration-300"
      >
        <h2 style={{ color: 'var(--text-title)' }} className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center transition-colors duration-300">
          <span className="w-2 h-6 bg-blue-500 rounded-full mr-3"></span>
          Доступные задания
        </h2>
        <TaskList refreshTrigger={tasksRefreshTrigger} onTaskStatusChange={refreshTasks} />
      </div>
      
      <div 
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-card)' }} 
        className="backdrop-blur-md p-4 sm:p-6 rounded-3xl border shadow-2xl shadow-black/20 transition-all duration-300"
      >
        <h2 style={{ color: 'var(--text-title)' }} className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center transition-colors duration-300">
          <span className="w-2 h-6 bg-orange-500 rounded-full mr-3"></span>
          Мои задания в работе
        </h2>
        <MyTasks refreshTrigger={tasksRefreshTrigger} onTaskStatusChange={refreshTasks} />
      </div>
    </div>
  ) : (
    <div className="space-y-6 sm:space-y-8">
      <div 
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-card)' }} 
        className="backdrop-blur-md p-4 sm:p-6 rounded-3xl border shadow-2xl shadow-black/20 transition-all duration-300"
      >
        <h2 style={{ color: 'var(--text-title)' }} className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center transition-colors duration-300">
          <span className="w-2 h-6 bg-green-500 rounded-full mr-3"></span>
          Создать новый заказ
        </h2>
        <CreateTaskForm onTaskCreated={refreshTasks} />
      </div>

      <div 
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-card)' }} 
        className="backdrop-blur-md p-4 sm:p-6 rounded-3xl border shadow-2xl shadow-black/20 transition-all duration-300"
      >
        <MyCreatedTasks refreshTrigger={tasksRefreshTrigger} />
      </div>
    </div>
  )}
</div>

            {isAdmin && (
              <div className="mt-16 sm:mt-20 border-t border-slate-800 pt-8 sm:pt-10">
                <h2 className="text-center font-bold text-slate-500 mb-6 uppercase tracking-widest text-sm">Панель администратора</h2>
                <AdminPanel user={user} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 💬 ВИДЖЕТ ЧАТА СООБЩЕСТВА */}
      {user && <CommunityChat currentUser={user} />}

      {/* 👤 КНОПКА МЕНЮ ПОЛЬЗОВАТЕЛЯ В ПРАВОМ НИЖНЕМ УГЛУ */}
      {user && <UserMenu currentTheme={theme} onToggleTheme={toggleTheme} user={user} onLogout={handleLogout} />}

      <Footer />
      <MessageListener />
    </div>
  );
}

export default App;